import { AuthUser } from '@aws-amplify/auth';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { InputMasked } from '@chiroup/components/form/InputMasked';
import { Phone } from '@chiroup/components/form/Phone';
import {
  ChiroUpAppointmentCommon,
  ChiroUpDayJsCommon,
} from '@chiroup/core/constants/stringConstants';
import { HealthComLocation } from '@chiroup/core/types/HealthComLocation.type';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useState } from 'react';
import { ScheduleForm } from './Container';
import Login, { SubmitButtonClassName } from './Login';
import SchedulePatients from './SchedulePatients';

type Props = {
  values: ScheduleForm;
  setValues: React.Dispatch<React.SetStateAction<ScheduleForm>>;
  onChange: (key: string) => (value: any) => void;
  treatmentName: string;
  providerName: string;
  providerImage?: string;
  appointmentDate: string;
  appointmentTime: string;
  makeChanges: () => void;
  setUser: (user: AuthUser) => void;
  path: string;
  getMe: () => Promise<void>;
  me: {
    patients: any[];
  } | null;
  confirm: () => void;
  isSaving: boolean;
  error: string | null;
  locationData: HealthComLocation;
  isCreditCardRequired: boolean;
  epayPublicKey: string | null;
};

const ScheduleConfirm = ({
  values,
  setValues,
  onChange,
  treatmentName,
  providerName,
  providerImage,
  appointmentDate,
  appointmentTime,
  makeChanges,
  setUser,
  path,
  getMe,
  me,
  confirm,
  isSaving,
  error,
  locationData,
  isCreditCardRequired,
  epayPublicKey,
}: Props) => {
  const [otherUserOpen, setOtherUserOpen] = useState(false);
  const [CCError, setCCError] = useState<string | null>(null);

  const okayToConfirm = () => {
    if (isCreditCardRequired) {
      if (!values.creditCardConsent) {
        return false;
      }
    }

    if (values.patient) {
      return true;
    }

    if (me) {
      return values.fname && values.lname;
    }

    return values.fname && values.lname && (values.dateOfBirth || '').length === 10;
  };

  const toggleOtherUserOpen = () => {
    setOtherUserOpen(prev => !prev);
  };

  const scheduleForSomeoneElse = () => {
    setValues?.((p: any) => ({
      ...p,
      patient: null,
    }));
    setOtherUserOpen(true);
  };

  const handlePaymentCardError = useCallback(
    (err: any) => {
      err = JSON.parse(err);

      if (err.code === '0') {
        setCCError(null);
      } else if (err.code) {
        // Send sentry the error
        Sentry.captureException(err, {
          tags: {
            component: 'ScheduleConfirm',
            error: err.message,
          },
        });
        setCCError(err.message);
      }
    },
    [setCCError]
  );

  useEffect(() => {
    let paymentCard: any = null;

    if ((window as any).usaepay && isCreditCardRequired) {
      const client = new (window as any).usaepay.Client(epayPublicKey);
      paymentCard = client.createPaymentCardEntry();

      const paymentCardConfig = {
        styles: ` 
        #payjs-container {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          padding: 0 !important;
          gap: 6px;
        }
        #payjs-input-icon {
          display: none;
        }
        #payjs-cnum-wrapper {
          grid-column: 1 / 12;
          width: 100%;
          padding: 0 !important;
        }
        #payjs-exp-wrapper {
          grid-column: 1 / 6;
          width: 100%;
          padding: 0 !important;
        }
        #payjs-cvv-wrapper {
          grid-column: 7 / 12;
          width: 100%;
          padding: 0 !important;
        }
        .payjs-container input {
          border: 1px solid !important;
          border-color: #d2d5db !important;
          border-radius: 4px !important;
          height: 36px;
          padding-left: 10px;
        }
        @media screen and (max-width: 400px) {
            .payjs-base {
                font-size: 12px;
            }
        }
          `,
      };

      paymentCard.generateHTML(paymentCardConfig);
      paymentCard.addHTML('paymentCardContainer');
      paymentCard.addEventListener('error', handlePaymentCardError);
    }
  }, [handlePaymentCardError, isCreditCardRequired, epayPublicKey]);

  return (
    <div className="flex flex-col sm:flex-row gap-12 text-left relative z-10" id="confirm">
      {isSaving && <div className="absolute left-0 right-0 grid place-items-center h-full z-20" />}
      <div className="flex flex-col gap-6 grow">
        <div className="grow">
          {me?.patients?.length && otherUserOpen ? (
            <div>
              <div
                onClick={toggleOtherUserOpen}
                className="cursor-pointer text-primary hover:underline flex flex-row gap-2 items-center text-sm"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to list</span>
              </div>
              <div className="mt-4">
                <div>
                  <Input
                    label="First name *"
                    type="text"
                    name="fname"
                    value={values.fname}
                    onChange={onChange('fname')}
                    className="col-span-1"
                  />
                </div>
                <div>
                  <Input
                    label="Last name *"
                    type="text"
                    name="lname"
                    value={values.lname}
                    onChange={onChange('lname')}
                    className="col-span-1"
                  />
                </div>
              </div>
              <div>
                <InputMasked
                  label="Date of birth"
                  name="dateOfBirth"
                  value={values.dateOfBirth}
                  onChange={e => {
                    setValues?.((p: any) => ({
                      ...p,
                      dateOfBirth: e,
                    }));
                  }}
                  patternFormat="##/##/####"
                  placeholder="MM/DD/YYYY"
                />
              </div>
            </div>
          ) : me?.patients?.length ? (
            <div className="flex flex-col gap-4">
              <SchedulePatients
                value={values.patient}
                onChange={onChange('patient')}
                patients={me?.patients || []}
              />
              <div className="w-full text-center text-sm">
                <div
                  onClick={scheduleForSomeoneElse}
                  className="cursor-pointer text-primary hover:underline"
                >
                  Need to schedule an appointment for someone else?
                </div>
              </div>
            </div>
          ) : me ? (
            <div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First name"
                  type="text"
                  name="fname"
                  value={values.fname}
                  onChange={onChange('fname')}
                  className="col-span-1"
                />
                <Input
                  label="Last name"
                  type="text"
                  name="lname"
                  value={values.lname}
                  onChange={onChange('lname')}
                  className="col-span-1"
                />
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={onChange('email')}
                  className="col-span-2"
                />
                <Phone
                  label="Phone"
                  name="phone"
                  value={values.phone}
                  onChange={onChange('phone')}
                  className="text-gray-900 font-medium col-span-2"
                  international
                />
              </div>
            </div>
          ) : (
            <Login
              setUser={setUser}
              path={path}
              getMe={getMe}
              onChange={onChange}
              values={values}
              setValues={setValues}
              clinicLocationData={locationData}
            />
          )}
        </div>
      </div>
      <div className="sm:w-64 md:w-72 lg:w-80 xl:w-96">
        <h2 className="sr-only">Summary</h2>
        <div className="rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5">
          <div className="flex flex-wrap divide-y divide-gray-900/5 w-full">
            <div className="flex flex-col p-6 w-full">
              <span className="text-xl font-semibold text-gray-900">Appointment information</span>
            </div>
            <div className="flex flex-col p-6 w-full gap-4">
              <div className="flex w-full flex-none gap-x-4">
                <div className="flex-none">
                  <span className="sr-only">Provider</span>
                  {providerImage ? (
                    <img
                      src={providerImage}
                      alt={providerName}
                      className="h-5 w-5 mt-[2px] border border-gray-400 rounded-full"
                    />
                  ) : (
                    <UserCircleIcon className="h-6 w-5 text-gray-400" />
                  )}
                </div>
                <div className="text-sm font-medium leading-6 text-gray-900">{providerName}</div>
              </div>
              <div className="flex w-full flex-none gap-x-4">
                <div className="flex-none">
                  <span className="sr-only">Treatment</span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="3 3 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-5 text-gray-400"
                  >
                    <path
                      d="M9 8C9 8.41421 9.33579 8.75 9.75 8.75C10.1642 8.75 10.5 8.41421 10.5 8H9ZM13.5 8C13.5 8.41421 13.8358 8.75 14.25 8.75C14.6642 8.75 15 8.41421 15 8H13.5ZM17.25 19.25L17.25 18.5H17.25V19.25ZM6.75 19.25L6.75 20H6.75V19.25ZM19.25 17.25L20 17.25V17.25H19.25ZM19.25 10L18.5 10V10H19.25ZM17.25 8L17.25 8.75L17.25 8ZM6.75 8L6.75 7.25L6.75 8ZM4.75 10L4 10L4.75 10ZM4.75 17.25L5.5 17.25L4.75 17.25ZM15.25 14.25C15.6642 14.25 16 13.9142 16 13.5C16 13.0858 15.6642 12.75 15.25 12.75V14.25ZM8.75 12.75C8.33579 12.75 8 13.0858 8 13.5C8 13.9142 8.33579 14.25 8.75 14.25V12.75ZM12.75 10.75C12.75 10.3358 12.4142 10 12 10C11.5858 10 11.25 10.3358 11.25 10.75L12.75 10.75ZM11.25 16.25C11.25 16.6642 11.5858 17 12 17C12.4142 17 12.75 16.6642 12.75 16.25H11.25ZM10.75 4.75L10.75 5.5L10.75 4.75ZM9.75 5.75L9 5.75V5.75H9.75ZM13.25 4.75L13.25 4L13.25 4.75ZM14.25 5.75L15 5.75L15 5.75L14.25 5.75ZM13.25 4L10.75 4L10.75 5.5L13.25 5.5L13.25 4ZM9 5.75V8H10.5V5.75H9ZM13.5 5.75V8H15V5.75H13.5ZM17.25 18.5H6.75V20H17.25V18.5ZM20 17.25V10H18.5V17.25H20ZM17.25 7.25L6.75 7.25L6.75 8.75L17.25 8.75L17.25 7.25ZM4 10L4 17.25L5.5 17.25L5.5 10L4 10ZM6.75 7.25C5.23122 7.25 4 8.48122 4 10L5.5 10C5.5 9.30964 6.05964 8.75 6.75 8.75L6.75 7.25ZM20 10C20 8.48122 18.7688 7.25 17.25 7.25L17.25 8.75C17.9404 8.75 18.5 9.30964 18.5 10L20 10ZM6.75 18.5C6.05964 18.5 5.5 17.9404 5.5 17.25L4 17.25C4 18.7688 5.23122 20 6.75 20L6.75 18.5ZM17.25 20C18.7688 20 20 18.7688 20 17.25L18.5 17.25C18.5 17.9404 17.9404 18.5 17.25 18.5L17.25 20ZM15.25 12.75H12V14.25H15.25V12.75ZM12 12.75H8.75V14.25H12V12.75ZM11.25 10.75L11.25 13.5L12.75 13.5L12.75 10.75L11.25 10.75ZM11.25 13.5V16.25H12.75V13.5H11.25ZM10.75 4C9.7835 4 9 4.7835 9 5.75L10.5 5.75C10.5 5.61193 10.6119 5.5 10.75 5.5L10.75 4ZM13.25 5.5C13.3881 5.5 13.5 5.61193 13.5 5.75L15 5.75C15 4.7835 14.2165 4 13.25 4L13.25 5.5Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </div>
                <div className="text-sm leading-6 text-gray-500">{treatmentName}</div>
              </div>
              <div className="flex w-full flex-none gap-x-4">
                <div className="flex-none">
                  <span className="sr-only">Date</span>
                  <CalendarDaysIcon className="h-6 w-5 text-gray-400" />
                </div>
                <div className="text-sm leading-6 text-gray-500">
                  {ChiroUpDayJsCommon.cleanupDatetime(appointmentDate)}
                </div>
              </div>
              <div className="flex w-full flex-none gap-x-4">
                <div className="flex-none">
                  <span className="sr-only">Time</span>
                  <ClockIcon className="h-6 w-5 text-gray-400" />
                </div>
                <div className="text-sm leading-6 text-gray-500">
                  {ChiroUpDayJsCommon.cleanupDatetime(appointmentTime)}
                </div>
              </div>
              {isCreditCardRequired && (
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Payment information required
                  </div>
                  <div id="paymentCardContainer" className="h-24">
                    {/* Payment Card iframe will go here */}
                  </div>
                  <div id="paymentCardErrorContainer">{/* Display error messages here */}</div>
                  <Checkbox
                    value={values.creditCardConsent}
                    onChange={onChange('creditCardConsent')}
                    label="I consent to storing my card for future use."
                    className="text-sm font-normal"
                    tooltip="By submitting your card, you consent to it being securely stored and authorize charges for services or fees. An authorization transaction for $0.01 may appear for a short time."
                  />
                </div>
              )}
              <div className="mt-4">
                {error || CCError ? (
                  <div className="rounded-md bg-white p-4 border border-red-300">
                    <div className="flex">
                      <div className="shrink-0">
                        <XCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-700">
                          There was an error confirming your appointment.
                        </h3>
                        {error === 'TIME_UNAVAILABLE' && (
                          <div className="mt-2 text-sm text-red-600">
                            The time that you selected is no longer available. Please go back and
                            select a new time.
                          </div>
                        )}
                        {error === ChiroUpAppointmentCommon.noSameDay.code && (
                          <div className="mt-2 text-sm text-red-600">
                            {ChiroUpAppointmentCommon.noSameDay.friendly}
                          </div>
                        )}
                        {error === ChiroUpAppointmentCommon.noPast.code && (
                          <div className="mt-2 text-sm text-red-600">
                            {ChiroUpAppointmentCommon.noPast.friendly}
                          </div>
                        )}
                        {error === ChiroUpAppointmentCommon.tooSoon.code && (
                          <div className="mt-2 text-sm text-red-600">
                            {ChiroUpAppointmentCommon.tooSoon.friendly}
                          </div>
                        )}
                        {typeof error === 'string' ? (
                          <div className="mt-2 text-sm text-red-600">{error}</div>
                        ) : CCError ? (
                          <div className="mt-2 text-sm text-red-600">{CCError}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="flex justify-left gap-2">
                  <Button
                    color={ButtonColors.plainWithBorder}
                    text="Change"
                    onClick={makeChanges}
                    disabled={isSaving && !error}
                    className={SubmitButtonClassName}
                  />
                  <Button
                    color={ButtonColors.plainWithBorder}
                    text="Confirm"
                    onClick={confirm}
                    loading={isSaving}
                    disabled={isSaving || !okayToConfirm()}
                    className={SubmitButtonClassName}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleConfirm;
